// Schutz vor geleakten Passwörtern (Frage 236, Meldung nach Frage 256, Daniel 13.10.2026).
//
// Ist der Schutz in Supabase an (Dashboard → Authentication → Attack Protection), lehnt Supabase beim
// Registrieren und beim Setzen oder Ändern eines Passworts jedes Passwort ab, das in bekannten Datenlecks steht
// (HaveIBeenPwned). Das gilt auch für die Admin-Schnittstelle (`auth.admin.createUser`, `updateUserById`), also
// für Edge Functions, die ein selbst gewähltes Passwort setzen. Supabase meldet dann den Fehlercode
// `weak_password` mit dem Grund `pwned` und dem englischen Satz unten. Alle Stellen, an denen ein Passwort
// gesetzt wird, zeigen stattdessen `PASSWORT_DATENLECK`; die eigenen Sätze für zu kurze oder zu einfache
// Passwörter bleiben, wie sie sind.
//
// Eine Regel an einer Stelle: Wer eine neue Seite mit Passwort baut, fragt `passwortFehlerText()`.

/** Was die Seite sagt, wenn Supabase ein Passwort aus einem Datenleck ablehnt (Daniel, Frage 256). */
export const PASSWORT_DATENLECK = "Dieses Passwort ist aus einem Datenleck bekannt. Bitte wähle ein anderes.";

/**
 * Der Satz, den Supabase Auth für den Grund `pwned` schickt (`internal/api/password.go`). Edge Functions reichen
 * ihn oft nur als Text weiter („Benutzer konnte nicht erstellt werden: Password is known to be weak …“).
 */
const PWNED_SATZ = /password is known to be weak and easy to guess/i;

const istObjekt = (w: unknown): w is Record<string, unknown> => typeof w === "object" && w !== null;
const hatPwned = (gruende: unknown): boolean => Array.isArray(gruende) && gruende.includes("pwned");

/**
 * Wurde das Passwort abgelehnt, weil es aus einem Datenleck bekannt ist? Erkennt den Fehler aus supabase-js
 * (`AuthWeakPasswordError`: `code` `weak_password`, `reasons` mit `pwned`), einen Antwortkörper mit
 * `weak_password.reasons` und den Satz von Supabase in einem Text (Fehler einer Edge Function).
 */
export function istPasswortAusDatenleck(fehler: unknown): boolean {
  if (typeof fehler === "string") return PWNED_SATZ.test(fehler);
  if (!istObjekt(fehler)) return false;
  if (fehler.code === "weak_password" && hatPwned(fehler.reasons)) return true;
  if (istObjekt(fehler.weak_password) && hatPwned(fehler.weak_password.reasons)) return true;
  for (const feld of ["message", "error", "msg"] as const) {
    if (typeof fehler[feld] === "string" && PWNED_SATZ.test(fehler[feld] as string)) return true;
  }
  return false;
}

/** `PASSWORT_DATENLECK`, wenn das Passwort aus einem Datenleck stammt, sonst der bisherige Text der Seite. */
export function passwortFehlerText(fehler: unknown, sonst: string): string {
  return istPasswortAusDatenleck(fehler) ? PASSWORT_DATENLECK : sonst;
}

const ZEICHEN = { gross: "ABCDEFGHJKLMNPQRSTUVWXYZ", klein: "abcdefghijkmnopqrstuvwxyz", ziffer: "23456789" };

/**
 * Ein zufälliges Passwort mit 16 Zeichen (Groß- und Kleinbuchstaben, Ziffern, ohne verwechselbare Zeichen), z. B.
 * als Vorgabe beim Anlegen eines Kontos im Admin. Die feste Vorgabe „1qay2wsx“ ist ein Tastaturmuster, das der Schutz
 * vor geleakten Passwörtern ablehnen würde; die Person legt ihr eigenes Passwort ohnehin über die Mail „Passwort
 * festlegen“ fest.
 */
export function zufallsPasswort(laenge = 16): string {
  const alle = ZEICHEN.gross + ZEICHEN.klein + ZEICHEN.ziffer;
  const zahlen = new Uint32Array(laenge);
  crypto.getRandomValues(zahlen);
  const aus = (vorrat: string, i: number) => vorrat[zahlen[i] % vorrat.length];
  // Je eine Stelle aus jeder Gruppe, damit auch strenge Regeln (Großbuchstabe, Ziffer) erfüllt sind.
  const zeichen = [aus(ZEICHEN.gross, 0), aus(ZEICHEN.klein, 1), aus(ZEICHEN.ziffer, 2)];
  for (let i = 3; i < laenge; i++) zeichen.push(aus(alle, i));
  return zeichen.join("");
}
